import { useState } from 'react'

import type { User } from '../../../services/usersManagement'
import type { FormUpdate, UserFormData } from '../hooks/useUserManagementForm'
import type { UserManagementConfig } from '../UserManagement.config'
import { USER_MANAGEMENT_DEFAULTS as D } from '../UserManagement.constants'

const INPUT_ERROR_CLASS = 'um-input--error'

type EmailValidationOptions = { allUsers: User[]; userId?: string }

export type UserInformationTabProps = {
    onNext: () => void
    onCancel: () => void
    isEditMode?: boolean
    isSelfEdit?: boolean
    userId?: string
    allUsers?: User[]
    submitting?: boolean
    formData: UserFormData
    update: FormUpdate
    config?: UserManagementConfig
    nextButtonLabel?: string
}

// Split-based validator (no regex backtracking risk) - accepts hyphenated/multi-level domains, e.g. edf-inc.com, middletongroup.com.au
function isValidEmail(email: string): boolean {
    const parts = email.split('@')
    if (parts.length !== 2) return false
    const [local, domain] = parts
    const domainParts = domain.split('.')
    return local.length > 0 && domainParts.length >= 2 && domainParts.every(p => p.length > 0)
}

function getEmailError(email: string, touched: boolean, options: EmailValidationOptions & { msgEmpty: string; msgFormat: string; msgExists: string }): string {
    if (!touched) return ''
    if (!email.trim()) return options.msgEmpty
    if (!isValidEmail(email)) return options.msgFormat
    const { allUsers, userId } = options
    if (allUsers.some(
        u => u.email?.toLowerCase() === email.toLowerCase() && String(u.id) !== String(userId ?? '')
    )) return options.msgExists
    return ''
}

function makeEmailOpts({ allUsers, isEditMode, userId }: { allUsers: User[]; isEditMode: boolean; userId?: string }, config: UserManagementConfig | undefined): EmailValidationOptions & { msgEmpty: string; msgFormat: string; msgExists: string } {
    return {
        allUsers,
        userId: isEditMode ? userId : undefined,
        msgEmpty: config?.userInformation?.validation?.emailEmpty ?? D.VALIDATION_EMAIL_EMPTY,
        msgFormat: config?.userInformation?.validation?.emailFormat ?? D.VALIDATION_EMAIL_FORMAT,
        msgExists: config?.userInformation?.validation?.emailExists ?? D.VALIDATION_EMAIL_EXISTS,
    }
}

function checkFormValid(formData: UserFormData, { allUsers, isEditMode, userId, config }: { allUsers: User[]; isEditMode: boolean; userId?: string; config?: UserManagementConfig }): boolean {
    const error = getEmailError(formData.email, true, makeEmailOpts({ allUsers, isEditMode, userId }, config))
    return formData.firstName.trim() !== '' &&
        formData.lastName.trim() !== '' &&
        formData.func.trim() !== '' &&
        isValidEmail(formData.email) &&
        !error
}

function fieldClass(touched: boolean, value: string): string {
    return touched && !value.trim() ? INPUT_ERROR_CLASS : ''
}

const UserInformationTab = ({ onNext, onCancel, isEditMode = false, isSelfEdit = false, userId, allUsers = [], submitting = false, formData, update, config, nextButtonLabel }: UserInformationTabProps) => {
    const [touched, setTouched] = useState(false)
    const [emailTouched, setEmailTouched] = useState(false)

    const gaShieldLabel = config?.userInformation?.gaShieldLabel ?? D.LABEL_GA_SHIELD
    const firstNameLabel = config?.userInformation?.firstNameLabel ?? D.LABEL_FIRST_NAME
    const lastNameLabel = config?.userInformation?.lastNameLabel ?? D.LABEL_LAST_NAME
    const functionLabel = config?.userInformation?.functionLabel ?? D.LABEL_FUNCTION
    const emailLabel = config?.userInformation?.emailLabel ?? D.LABEL_EMAIL
    const cancelLabel = config?.cancelLabel ?? D.BTN_CANCEL
    const nextLabel = nextButtonLabel ?? config?.nextLabel ?? D.BTN_NEXT

    const emailError = getEmailError(formData.email, emailTouched || touched, makeEmailOpts({ allUsers, isEditMode, userId }, config))

    const validationFirstName = config?.userInformation?.validation?.firstName ?? D.VALIDATION_FIRST_NAME
    const validationLastName = config?.userInformation?.validation?.lastName ?? D.VALIDATION_LAST_NAME
    const validationFunction = config?.userInformation?.validation?.function ?? D.VALIDATION_FUNCTION

    const handleNext = () => {
        setTouched(true)
        setEmailTouched(true)
        checkFormValid(formData, { allUsers, isEditMode, userId, config }) && onNext()
    }

    return (
        <div className="um-form-section">
            <div className="um-checkbox-row">
                <input
                    type="checkbox"
                    id="gaShield"
                    checked={formData.isGAShieldUser}
                    disabled={isSelfEdit}
                    onChange={e => update('isGAShieldUser', e.target.checked)}
                />
                <label htmlFor="gaShield">{gaShieldLabel}</label>
            </div>

            <div className="form-group">
                <label htmlFor="firstName"><span className="required-mark">*</span>{firstNameLabel}</label>
                <input
                    type="text"
                    id="firstName"
                    placeholder={D.PLACEHOLDER_FIRST_NAME}
                    value={formData.firstName}
                    onChange={e => update('firstName', e.target.value)}
                    onBlur={() => setTouched(true)}
                    className={fieldClass(touched, formData.firstName)}
                />
                {touched && !formData.firstName.trim() && (
                    <p className="field-error"><span className="field-error__icon">i</span>{validationFirstName}</p>
                )}
            </div>

            <div className="form-group">
                <label htmlFor="lastName"><span className="required-mark">*</span>{lastNameLabel}</label>
                <input
                    type="text"
                    id="lastName"
                    placeholder={D.PLACEHOLDER_LAST_NAME}
                    value={formData.lastName}
                    onChange={e => update('lastName', e.target.value)}
                    onBlur={() => setTouched(true)}
                    className={fieldClass(touched, formData.lastName)}
                />
                {touched && !formData.lastName.trim() && (
                    <p className="field-error"><span className="field-error__icon">i</span>{validationLastName}</p>
                )}
            </div>

            <div className="form-group">
                <label htmlFor="func"><span className="required-mark">*</span>{functionLabel}</label>
                <input
                    type="text"
                    id="func"
                    placeholder={D.PLACEHOLDER_FUNCTION}
                    value={formData.func}
                    onChange={e => update('func', e.target.value)}
                    onBlur={() => setTouched(true)}
                    className={fieldClass(touched, formData.func)}
                />
                {touched && !formData.func.trim() && (
                    <p className="field-error"><span className="field-error__icon">i</span>{validationFunction}</p>
                )}
            </div>

            <div className="form-group">
                <label htmlFor="email"><span className="required-mark">*</span>{emailLabel}</label>
                <input
                    type="email"
                    id="email"
                    placeholder={D.PLACEHOLDER_EMAIL}
                    value={formData.email}
                    disabled={isSelfEdit || isEditMode}
                    onChange={e => update('email', e.target.value)}
                    onBlur={() => setEmailTouched(true)}
                    className={emailError ? INPUT_ERROR_CLASS : ''}
                />
                {emailError && (
                    <p className="field-error"><span className="field-error__icon">i</span>{emailError}</p>
                )}
            </div>

            <div className="button-group">
                <button className="btn btn-cancel" onClick={onCancel}>{cancelLabel}</button>
                <button className="btn btn-add" onClick={handleNext} disabled={submitting}>{nextLabel}</button>
            </div>
        </div>
    )
}

export default UserInformationTab

